import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/admin.css";

const EditProduct = () => {
    const [products, setProducts] = useState([]);
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(null);

    const [images, setImages] = useState([]);
    const [newImages, setNewImages] = useState([]);

    const navigate = useNavigate();
    const backendUrl = process.env.REACT_APP_BACKEND_URI;

    /* ================= AUTH ================= */
    useEffect(() => {
        const token = localStorage.getItem("adminToken");
        if (!token) navigate("/");
    }, [navigate]);

    /* ================= FETCH ================= */
    const fetchProducts = () => {
        fetch(`${backendUrl}/products`)
            .then(res => res.json())
            .then(setProducts)
            .catch(console.error);
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    /* ================= MODAL ================= */
    const openModal = async (product) => {
        const res = await fetch(`${backendUrl}/products/${product._id}`);
        const data = await res.json();

        setCurrent({
            ...data,
            content_sections:
                Array.isArray(data.content_sections) &&
                    data.content_sections.length
                    ? data.content_sections.map(s => ({
                        title: s.title || "",
                        sub_title: s.sub_title || "",
                        contents:
                            Array.isArray(s.contents) && s.contents.length
                                ? s.contents
                                : [""],
                    }))
                    : [{ title: "", sub_title: "", contents: [""] }],
        });

        setImages(data.images || []);
        setNewImages([]);
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setCurrent(null);
        setImages([]);
        setNewImages([]);
    };

    /* ================= IMAGE ================= */
    const removeImage = (index) =>
        setImages(images.filter((_, i) => i !== index));

    const handleFileChange = (e) =>
        setNewImages(prev => [...prev, ...Array.from(e.target.files)]);

    /* ================= CONTENT SECTIONS ================= */
    const updateField = (si, field, value) => {
        const updated = [...current.content_sections];
        updated[si][field] = value;
        setCurrent({ ...current, content_sections: updated });
    };

    const updateContent = (si, ci, value) => {
        const updated = [...current.content_sections];
        updated[si].contents[ci] = value;
        setCurrent({ ...current, content_sections: updated });
    };

    const addContent = (si) => {
        const updated = [...current.content_sections];
        updated[si].contents.push("");
        setCurrent({ ...current, content_sections: updated });
    };

    const removeContent = (si, ci) => {
        const updated = [...current.content_sections];
        updated[si].contents = updated[si].contents.filter(
            (_, i) => i !== ci
        );
        setCurrent({ ...current, content_sections: updated });
    };

    const addSection = () => {
        setCurrent({
            ...current,
            content_sections: [
                ...current.content_sections,
                { title: "", sub_title: "", contents: [""] },
            ],
        });
    };

    const removeSection = (si) => {
        setCurrent({
            ...current,
            content_sections: current.content_sections.filter(
                (_, i) => i !== si
            ),
        });
    };

    /* ================= UPDATE ================= */
    const updateProduct = async () => {
        if (current.is_latest_product) {
            for (const section of current.content_sections) {
                if (!section.title.trim()) {
                    alert("Section title is required");
                    return;
                }
                if (!section.sub_title.trim()) {
                    alert("Section sub title is required");
                    return;
                }
                if (section.contents.some(c => !c.trim())) {
                    alert("All content fields are required");
                    return;
                }
            }
        }

        const formData = new FormData();
        formData.append("title", current.title);
        formData.append("is_latest_product", current.is_latest_product);
        formData.append(
            "content_sections",
            JSON.stringify(current.content_sections)
        );
        formData.append("existingImages", JSON.stringify(images));

        newImages.forEach(img => formData.append("images", img));

        const res = await fetch(
            `${backendUrl}/products/${current._id}`,
            {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
                },
                body: formData,
            }
        );

        if (!res.ok) {
            alert("Failed to update product");
            return;
        }

        alert("Product updated successfully");
        fetchProducts();
        closeModal();
    };

    /* ================= UI ================= */
    return (
        <div className="page-content">
            <h2>Edit Product</h2>

            <table className="admin-table" style={{ textAlign: "center" }}>
                <thead>
                    <tr>
                        <th>Photo</th>
                        <th>Title</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map(p => (
                        <tr key={p._id}>
                            <td>
                                <img
                                    src={p.images?.[0]}
                                    alt=""
                                    style={{
                                        width: 50,
                                        height: 50,
                                        objectFit: "cover",
                                    }}
                                />
                            </td>
                            <td>{p.title}</td>
                            <td>
                                <button
                                    className="edit-btn"
                                    onClick={() => openModal(p)}
                                >
                                    Edit
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {open && current && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal" style={{ maxWidth: 540 }}>
                        <div className="modal-header">
                            <h3>Update Product</h3>
                            <button className="close-x" onClick={closeModal}>
                                ✕
                            </button>
                        </div>

                        <div className="admin-form">
                            {/* IMAGES */}
                            <div className="image-grid">
                                {images.map((img, i) => (
                                    <div className="image-box" key={i}>
                                        <img src={img} alt="" />
                                        <button
                                            className="remove-btn"
                                            onClick={() => removeImage(i)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <input type="file" multiple onChange={handleFileChange} />

                            <label>Title</label>
                            <input
                                value={current.title}
                                onChange={e =>
                                    setCurrent({
                                        ...current,
                                        title: e.target.value,
                                    })
                                }
                            />

                            <label className="checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={current.is_latest_product}
                                    onChange={e =>
                                        setCurrent({
                                            ...current,
                                            is_latest_product: e.target.checked,
                                        })
                                    }
                                />
                                <span>Latest Product</span>
                            </label>

                            {current.is_latest_product && (
                                <>
                                    <label>Content Sections</label>

                                    {current.content_sections.map((s, si) => (
                                        <div key={si} style={{ marginBottom: 16 }}>
                                            <input
                                                placeholder="Section Title"
                                                value={s.title}
                                                onChange={e =>
                                                    updateField(
                                                        si,
                                                        "title",
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            <input
                                                placeholder="Section Sub Title"
                                                style={{ marginTop: 6 }}
                                                value={s.sub_title}
                                                onChange={e =>
                                                    updateField(
                                                        si,
                                                        "sub_title",
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            {s.contents.map((c, ci) => (
                                                <div
                                                    key={ci}
                                                    style={{
                                                        display: "flex",
                                                        gap: 10,
                                                        marginTop: 8,
                                                    }}
                                                >
                                                    <input
                                                        placeholder="Content"
                                                        value={c}
                                                        onChange={e =>
                                                            updateContent(
                                                                si,
                                                                ci,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    {ci !== 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                removeContent(
                                                                    si,
                                                                    ci
                                                                )
                                                            }
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}

                                            <button
                                                type="button"
                                                onClick={() => addContent(si)}
                                            >
                                                + Add Content
                                            </button>

                                            {si !== 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeSection(si)
                                                    }
                                                    style={{ marginLeft: 10 }}
                                                >
                                                    Delete Section
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    <button type="button" onClick={addSection}>
                                        + Add Section
                                    </button>
                                </>
                            )}

                            <div className="modal-actions">
                                <button
                                    className="admin-submit-btn"
                                    onClick={updateProduct}
                                >
                                    Save Changes
                                </button>
                                <button
                                    className="cancel-btn"
                                    onClick={closeModal}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditProduct;
